import PDFKit from 'pdfkit';
import {
  col3,
  COLOR_LIGHT,
  colorKeyBlock,
  deref,
  FONT,
  GAP_DESCRIPTION,
  GAP_NORMAL,
  hline,
  MIN_PAGE_SPACE,
  PAGE_HEIGHT,
  PAGE_MARGINS,
  SIZE_DESCRIPTION,
  SIZE_KEY,
} from '@/lib/pdf/helpers';
import { format as formatCurrency } from '@/lib/currency';
import { BarGraph } from '@/lib/graphs';
import { CalculationState } from '@/lib/calculationstate';
import { getPayoutColor, getPayoutDescription, getPayoutName } from '@/lib/payout';

// Disable param reassign because working with doc becomes horrific otherwise
/* eslint no-param-reassign: 0 */

/**
 * Draws the yearly graphs as a table form on the PDF.
 * Will attempt to add pagebreaks when not enough space is available, see `MIN_PAGE_SPACE` for a tweakable.
 *
 * After calling this function the cursor position is undefined.
 *
 * @param doc
 * @param state
 * @param graph
 */
export default function table(doc: PDFKit.PDFDocument, state: CalculationState, graph: BarGraph) {
  // Terrible estimate, assumes we need 5 lines of text
  const estimateHeight = doc.currentLineHeight(true) * 5;
  if (doc.y >= PAGE_HEIGHT - PAGE_MARGINS - estimateHeight - MIN_PAGE_SPACE) {
    doc.addPage();
  }

  const { y } = doc;
  doc
    .lineGap(GAP_NORMAL)
    .font(FONT)
    .fontSize(SIZE_KEY)
    .text(deref(graph.name), col3(0), y, {
      width: col3(1) - col3(0),
    });

  const totalValue = graph.segments.reduce((acc, segment) => acc + deref(segment.value), 0);
  const totalPercent = Math.round((totalValue / state.salary) * 100);
  doc.text(`${formatCurrency(totalValue)} (${totalPercent}%)`, col3(1), y);

  const description = deref(graph.description).join('\n');
  doc
    .lineGap(GAP_DESCRIPTION)
    .fontSize(SIZE_DESCRIPTION)
    .text(description, col3(2), y);

  graph.segments.filter((segment) => deref(segment.value) > 0).forEach((segment, i) => {
    // If we didn't write a description, do not draw a line for the initial block
    // Draw lines for all other blocks regardless
    if (i !== 0 || description !== '') {
      hline(doc, {
        x: col3(2),
        color: COLOR_LIGHT,
        padding: 5,
      });
      doc.moveDown();
    }

    colorKeyBlock(doc, getPayoutColor(segment.category), getPayoutName(segment.category), deref(segment.value));

    doc
      .lineGap(GAP_DESCRIPTION)
      .fontSize(SIZE_DESCRIPTION)
      .text(getPayoutDescription(segment.category).join('\n'))
      .moveDown();
  });

  hline(doc, { color: COLOR_LIGHT });
  doc.moveDown();
}
